import { useState } from "react";

import { ChevronDown, ChevronUp } from "../../components/icons/svg/outline";
import { MetricTiles } from "../../components/ui";

/**
 * Turn expanded details into titled columns.
 *
 * Entries that already have an ``items`` array stay as their own column.
 * A flat list of label/value lines becomes one untitled column.
 *
 * @param {Array<Object>} details - Category columns or flat detail lines.
 * @returns {Array<{key: string, title: string, items: Array<Object>}>}
 */
function groupExpandedDetails(details) {
  if (!Array.isArray(details) || details.length === 0) return [];
  if (details.some((item) => Array.isArray(item.items))) {
    return details
      .filter((item) => Array.isArray(item.items) && item.items.length > 0)
      .map((item, index) => ({
        key: item.key ?? item.title ?? index,
        title: item.title || "",
        items: item.items,
      }));
  }
  return [{ key: "details", title: "", items: details }];
}

/**
 * Place category groups down each column so a short group does not stretch the row.
 *
 * Groups keep their current order across the row, then continue in that same
 * column. Two groups side by side stay paired with whatever follows them.
 *
 * @param {Array<Object>} groups - Category groups in display order.
 * @param {number} columnCount - How many columns to fill.
 * @returns {Array<Array<Object>>}
 */
function packIntoColumns(groups, columnCount) {
  const columns = Array.from({ length: columnCount }, () => []);
  groups.forEach((group, index) => {
    columns[index % columnCount].push(group);
  });
  return columns.filter((column) => column.length > 0);
}

/**
 * Compact metric bar that starts folded and can open quieter detail lines.
 *
 * Folded metrics stay in boxes. Expanded metrics, when passed, stay in boxes
 * too. Expanded details are plain label/value lines with no tile chrome.
 * A detail entry with an ``items`` array is a category column and a title.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.foldedMetrics - Metrics visible before expanding.
 * @param {Array<Object>} [props.expandedMetrics] - Boxed metrics revealed when open.
 * @param {Array<Object>} [props.expandedDetails] - Quiet lines, or category columns
 *   shaped as ``{ key, title, items }``.
 * @param {boolean} [props.defaultExpanded=false] - Whether the detail row starts open.
 * @param {string} [props.title="Overview"] - Heading beside the toggle.
 * @returns {JSX.Element}
 */
export default function CollapsibleStatsBar({
  foldedMetrics,
  expandedMetrics = [],
  expandedDetails = [],
  defaultExpanded = false,
  title = "Overview",
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const Chevron = expanded ? ChevronUp : ChevronDown;
  const detailGroups = groupExpandedDetails(expandedDetails);
  const detailColumns = packIntoColumns(detailGroups, 2);
  const canExpand = expandedMetrics.length > 0 || detailGroups.length > 0;

  return (
    <section className="rounded-lg bg-white px-4 py-3 shadow-md shadow-gray-300">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {canExpand ? (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? "Show less" : "Show more"}
            <Chevron className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      <MetricTiles metrics={foldedMetrics} mobileColumns={2} />
      {expanded && expandedMetrics.length > 0 ? (
        <div className="mt-2">
          <MetricTiles metrics={expandedMetrics} mobileColumns={2} />
        </div>
      ) : null}
      {expanded && detailColumns.length > 0 ? (
        <div className="mt-3 grid grid-cols-1 gap-x-8 border-t border-gray-100 pt-3 sm:grid-cols-2">
          {detailColumns.map((column) => (
            <div key={column[0].key} className="flex min-w-0 flex-col gap-4">
              {column.map((group) => (
                <div key={group.key} className="min-w-0">
                  {group.title ? (
                    <h3 className="mb-1 text-xs font-semibold text-gray-700">{group.title}</h3>
                  ) : null}
                  <dl className="space-y-1 text-xs text-gray-500">
                    {group.items.map((item) => (
                      <div
                        key={item.key ?? item.label}
                        className="flex min-w-0 items-baseline justify-between gap-3"
                      >
                        <dt className="min-w-0 truncate">{item.label}</dt>
                        <dd
                          className="max-w-[55%] shrink-0 truncate text-right text-gray-700"
                          title={item.title || undefined}
                        >
                          {item.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
